import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TrebbleButton from '../common/TrebbleButton';
import TrebbleInput from '../common/TrebbleInput';
import TrebbleCard from '../common/TrebbleCard';
import TrebbleSelect from '../common/TrebbleSelect';
import TrebbleSwitch from '../common/TrebbleSwitch';
import TrebbleTooltip from '../common/TrebbleTooltip';
import TrebbleDivider from '../common/Divider';
import ModalHeader from '../common/ModalHeader';
import Utils from '../../models/helper/Utils';

/**
 * WorkspaceMembersContent displays a team members list with invite, role change,
 * admin toggle, and remove capabilities. Meant to be rendered inside an OptionPopupView.
 *
 * @param {Object} props
 * @param {Array} props.members - WorkspaceMemberInfo[]
 * @param {boolean} props.isOwnerOrAdmin - Whether current user can manage members
 * @param {number|null} props.seatLimit - Maximum number of editor seats (null = unlimited)
 * @param {number} props.seatUsed - Number of editor seats currently used
 * @param {function} props.onInvite - (email, role) => Promise<void>
 * @param {function} props.onRemove - (memberRecordId) => Promise<void>
 * @param {function} props.onSetRole - (memberRecordId, newRole) => Promise<void>
 * @param {function} props.onSetAdmin - (memberRecordId, isAdmin) => Promise<void>
 * @param {function} props.onClose - () => void
 * @param {Object} props.i18nModule - i18n module for translations
 * @param {string} props.mode - "manage" or "invite"
 * @param {boolean} props.useEditorTheme - Whether to use editor theme
 */
const WorkspaceMembersContent = ({
    members,
    isOwnerOrAdmin,
    seatLimit,
    seatUsed,
    mode,
    onInvite,
    onRemove,
    onSetRole,
    onSetAdmin,
    onClose,
    i18nModule,
    useEditorTheme = false,
}) => {
    const [email, setEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('editor');
    const [isLoading, setIsLoading] = useState(false);
    const atSeatLimit = seatLimit != null && seatUsed >= seatLimit;
    const [errorMessage, setErrorMessage] = useState(null);
    const [loadingMemberId, setLoadingMemberId] = useState(null);
    const isInviteOnlyMode = mode === 'invite';

    const t = (key) => {
        return window.getI18n(i18nModule, key);
    };

    // Extract a stable backend error code from a failed mutation response.
    // The Trebble Node server surfaces error codes in a few historically
    // different shapes — check them all and return the first match so we can
    // switch on a single value. WorkspaceBillingHelper/WorkspaceMember forward
    // the code as `errorCode` on the ExpectedError instance, and the response
    // envelope may expose it as `errorCode` or `code` on any nesting level.
    const getErrorCode = (error) => {
        if (!error) return null;
        if (typeof error.errorCode === 'string') return error.errorCode;
        if (typeof error.code === 'string') return error.code;
        if (error.responseJSON && typeof error.responseJSON.errorCode === 'string') return error.responseJSON.errorCode;
        if (error.responseJSON && typeof error.responseJSON.code === 'string') return error.responseJSON.code;
        if (error.data && typeof error.data.errorCode === 'string') return error.data.errorCode;
        if (error.data && typeof error.data.code === 'string') return error.data.code;
        if (error.error && typeof error.error.errorCode === 'string') return error.error.errorCode;
        if (error.error && typeof error.error.code === 'string') return error.error.code;
        return null;
    };

    const handleInvite = async () => {
        if (!email.trim()) {
            return;
        }
        if (atSeatLimit && inviteRole === 'editor') {
            window.alertErrorMessage(t('SEAT_LIMIT_REACHED'));
            return;
        }
        if (inviteRole === 'editor') {
            const confirmed = await Utils.getInstance().showUserConfirmationDialog(
                t('INVITE_MEMBER'),
                t('PAID_SEAT_WARNING')
            );
            if (!confirmed) return;
        }
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onInvite(email.trim(), inviteRole);
            setEmail('');
        } catch (error) {
            // Map specific backend error codes to actionable i18n strings so the
            // client-only seat check can't diverge from server enforcement.
            const code = getErrorCode(error);
            let message;
            if (code === 'workspace/seat_limit_reached') {
                message = t('SEAT_LIMIT_REACHED_SERVER');
            } else if (code === 'workspace/invite_already_pending') {
                message = t('INVITE_ALREADY_PENDING');
            } else if (code === 'workspace/member_already_exists') {
                message = t('MEMBER_ALREADY_EXISTS');
            } else {
                message = error instanceof Error ? error.message : t('INVITE_ERROR');
            }
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemove = async (memberRecordId) => {
        setLoadingMemberId(memberRecordId);
        setErrorMessage(null);
        try {
            await onRemove(memberRecordId);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('REMOVE_ERROR');
            setErrorMessage(message);
        } finally {
            setLoadingMemberId(null);
        }
    };

    const handleSetRole = async (memberRecordId, newRole) => {
        setLoadingMemberId(memberRecordId);
        setErrorMessage(null);
        try {
            await onSetRole(memberRecordId, newRole);
        } catch (error) {
            const code = getErrorCode(error);
            const message = code === 'workspace/seat_limit_reached'
                ? t('SEAT_LIMIT_REACHED_SERVER')
                : (error instanceof Error ? error.message : t('ROLE_ERROR'));
            setErrorMessage(message);
        } finally {
            setLoadingMemberId(null);
        }
    };

    const handleSetAdmin = async (memberRecordId, isAdmin) => {
        setLoadingMemberId(memberRecordId);
        setErrorMessage(null);
        try {
            await onSetAdmin(memberRecordId, isAdmin);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('ADMIN_ERROR');
            setErrorMessage(message);
        } finally {
            setLoadingMemberId(null);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && email.trim()) {
            handleInvite();
        }
    };

    const getRoleBadgeStyle = (role) => {
        const baseStyle = {
            display: 'inline-block',
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        };
        if (role === 'owner') {
            return { ...baseStyle, backgroundColor: '#e6f7ff', color: '#1890ff' };
        }
        if (role === 'admin') {
            return { ...baseStyle, backgroundColor: '#fff7e6', color: '#fa8c16' };
        }
        if (role === 'editor') {
            return { ...baseStyle, backgroundColor: '#f6ffed', color: '#52c41a' };
        }
        if (role === 'reviewer') {
            return { ...baseStyle, backgroundColor: '#f3edff', color: '#7E57C2' };
        }
        return { ...baseStyle, backgroundColor: '#f5f5f5', color: '#8c8c8c' };
    };

    const getAdminBadgeStyle = () => ({
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        backgroundColor: '#fff7e6',
        color: '#fa8c16',
        marginLeft: '6px',
    });

    const getStatusBadgeStyle = () => ({
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 500,
        fontStyle: 'italic',
        color: '#8c8c8c',
        marginLeft: '6px',
    });

    const getRoleLabel = (role) => {
        if (role === 'owner') return t('OWNER_ROLE');
        if (role === 'admin') return t('ADMIN_ROLE');
        if (role === 'editor') return t('EDITOR_ROLE');
        if (role === 'reviewer') return t('REVIEWER_ROLE') || 'Reviewer';
        return t('VIEWER_ROLE');
    };

    const renderMemberRow = (member) => {
        const isOwner = member.role === 'owner';
        const isViewer = member.role === 'viewer';
        const isMemberLoading = loadingMemberId === member.memberRecordId;
        const displayName = member.username || member.email;
        const showControls = isOwnerOrAdmin && !isOwner && !isViewer;
        const editableRole = member.role === 'admin' ? 'editor' : member.role;

        return (
            <div
                key={member.memberRecordId}
                className="flex items-center gap-3 py-3"
                style={{
                    borderBottom: '1px solid var(--color-black-10)',
                    opacity: isMemberLoading ? 0.5 : 1,
                }}
            >
                {/* Name & badges */}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                        {displayName}
                    </div>
                    {member.username && member.email && (
                        <div className="text-xs text-gray-400 mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                            {member.email}
                        </div>
                    )}
                    <div className="mt-1 flex items-center flex-wrap">
                        <span style={getRoleBadgeStyle(member.role)}>
                            {getRoleLabel(member.role)}
                        </span>
                        {member.isAdmin && (
                            <span style={getAdminBadgeStyle()}>
                                {t('ADMIN')}
                            </span>
                        )}
                        {(member.status === 'invited' || member.status === 'pending') && (
                            <span style={getStatusBadgeStyle()}>
                                {t('PENDING')}
                            </span>
                        )}
                    </div>
                </div>

                {/* Controls: role dropdown, admin toggle, remove button */}
                {showControls && (
                    <div className="flex items-center gap-2 shrink-0">
                        <TrebbleSelect
                            value={editableRole}
                            onChange={(value) => handleSetRole(member.memberRecordId, value)}
                            disabled={isMemberLoading}
                            useEditorTheme={useEditorTheme}
                            style={{ width: 110 }}
                            size="small"
                        >
                            <TrebbleSelect.Option value="editor">{t('EDITOR_ROLE')}</TrebbleSelect.Option>
                            <TrebbleSelect.Option value="reviewer">{t('REVIEWER_ROLE') || 'Reviewer'}</TrebbleSelect.Option>
                        </TrebbleSelect>

                        <TrebbleTooltip title={t('ADMIN_TOOLTIP')} useEditorTheme={useEditorTheme}>
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                {t('ADMIN')}
                                <TrebbleSwitch
                                    checked={member.isAdmin}
                                    onChange={(checked) => handleSetAdmin(member.memberRecordId, checked)}
                                    disabled={isMemberLoading}
                                    useEditorTheme={useEditorTheme}
                                    size="small"
                                />
                            </span>
                        </TrebbleTooltip>

                        <TrebbleTooltip title={t('REMOVE_MEMBER')} useEditorTheme={useEditorTheme}>
                            <span>
                                <TrebbleButton
                                    aria-label={t('REMOVE_MEMBER')}
                                    type="text"
                                    danger
                                    size="small"
                                    onClick={() => handleRemove(member.memberRecordId)}
                                    disabled={isMemberLoading}
                                    useEditorTheme={useEditorTheme}
                                    style={{ minWidth: 0, padding: '0 6px' }}
                                >
                                    <span style={{ fontSize: '16px', lineHeight: 1 }}>&#x2715;</span>
                                </TrebbleButton>
                            </span>
                        </TrebbleTooltip>
                    </div>
                )}
            </div>
        );
    };

    const allMembers = members || [];
    const activeMembers = allMembers.filter(function(m) { return m.status !== 'invited' && m.status !== 'pending'; });
    const pendingMembers = allMembers.filter(function(m) { return m.status === 'invited' || m.status === 'pending'; });
    const memberCount = activeMembers.length;

    const renderInviteForm = () => {
        if (!isOwnerOrAdmin) {
            return null;
        }

        if (isInviteOnlyMode) {
            return (
                <div className="flex flex-col gap-3">
                    <div className="flex gap-2 items-center">
                        <TrebbleInput
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('EMAIL_PLACEHOLDER')}
                            useEditorTheme={useEditorTheme}
                            disabled={isLoading}
                            style={{ flex: 1 }}
                        />
                        <TrebbleSelect
                            value={inviteRole}
                            onChange={(value) => setInviteRole(value)}
                            disabled={isLoading}
                            useEditorTheme={useEditorTheme}
                            style={{ width: 110 }}
                        >
                            <TrebbleSelect.Option value="editor">{t('EDITOR_ROLE')}</TrebbleSelect.Option>
                        </TrebbleSelect>
                    </div>

                    {atSeatLimit && inviteRole === 'editor' && (
                        <div className="text-xs" style={{ color: 'var(--color-error-bright)' }}>
                            {t('SEAT_LIMIT_REACHED')}
                        </div>
                    )}

                    <div className="flex gap-3 justify-end pt-2">
                        <TrebbleButton
                            onClick={onClose}
                            useEditorTheme={useEditorTheme}
                            disabled={isLoading}
                        >
                            {t('CANCEL')}
                        </TrebbleButton>
                        <TrebbleButton
                            type="primary"
                            onClick={handleInvite}
                            useEditorTheme={useEditorTheme}
                            loading={isLoading}
                            disabled={!email.trim() || (atSeatLimit && inviteRole === 'editor')}
                        >
                            {t('SEND_INVITE')}
                        </TrebbleButton>
                    </div>
                </div>
            );
        }

        return (
            <div style={{ borderTop: '1px solid var(--color-black-10)', paddingTop: '16px' }}>
                <div className="text-sm font-bold mb-3">
                    {t('INVITE_MEMBER')}
                </div>

                <div className="flex gap-2 items-center mb-2">
                    <TrebbleInput
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('EMAIL_PLACEHOLDER')}
                        useEditorTheme={useEditorTheme}
                        disabled={isLoading}
                        style={{ flex: 1 }}
                    />
                    <TrebbleSelect
                        value={inviteRole}
                        onChange={(value) => setInviteRole(value)}
                        disabled={isLoading}
                        useEditorTheme={useEditorTheme}
                        style={{ width: 110 }}
                    >
                        <TrebbleSelect.Option value="editor">{t('EDITOR_ROLE')}</TrebbleSelect.Option>
                    </TrebbleSelect>
                </div>

                {atSeatLimit && inviteRole === 'editor' ? (
                    <div className="text-xs mb-3" style={{ color: 'var(--color-error-bright)' }}>
                        {t('SEAT_LIMIT_REACHED')}
                    </div>
                ) : (
                    <div className="text-xs text-gray-400 mb-3">
                        {t('MEMBER_SEAT_NOTE')}
                    </div>
                )}

                <div className="flex gap-3 justify-end">
                    <TrebbleButton
                        onClick={onClose}
                        useEditorTheme={useEditorTheme}
                        disabled={isLoading}
                    >
                        {t('CANCEL')}
                    </TrebbleButton>
                    <TrebbleButton
                        type="primary"
                        onClick={handleInvite}
                        useEditorTheme={useEditorTheme}
                        loading={isLoading}
                        disabled={!email.trim() || (atSeatLimit && inviteRole === 'editor')}
                    >
                        {t('SEND_INVITE')}
                    </TrebbleButton>
                </div>
            </div>
        );
    };

    if (isInviteOnlyMode) {
        return (
            <TrebbleCard
                useEditorTheme={useEditorTheme}
                className="modal_content"
            >
                <div className="flex flex-col">
                    <ModalHeader
                        title={t('INVITE_MEMBER')}
                        useEditorTheme={useEditorTheme}
                        marginBottom={0}
                    />

                    <TrebbleDivider useEditorTheme={useEditorTheme} style={{ margin: '12px 0 16px 0' }} />

                    {errorMessage && (
                        <div className="text-[13px] mb-3" style={{ color: 'var(--color-error-bright)' }}>
                            {errorMessage}
                        </div>
                    )}

                    {renderInviteForm()}
                </div>
            </TrebbleCard>
        );
    }

    return (
        <TrebbleCard
            useEditorTheme={useEditorTheme}
            className="modal_content"
            style={{ maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}
        >
            <div className="flex flex-col">
                {/* Header */}
                <ModalHeader
                    title={t('TEAM_MEMBERS')}
                    subtitle={t('MEMBER_COUNT').replace('${count}', String(memberCount))}
                    useEditorTheme={useEditorTheme}
                    marginBottom={0}
                />

                <TrebbleDivider useEditorTheme={useEditorTheme} style={{ margin: '12px 0 16px 0' }} />

                {/* Member list */}
                <div style={{ flex: 1, overflowY: 'auto', marginBottom: isOwnerOrAdmin ? '16px' : '0' }}>
                    {activeMembers.length > 0 ? (
                        activeMembers.map(renderMemberRow)
                    ) : (
                        <div className="py-5 text-center text-gray-400 text-[13px]">
                            {t('NO_MEMBERS')}
                        </div>
                    )}
                    {pendingMembers.length > 0 && (
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 py-3 pb-1">
                                {t('PENDING_INVITES')}
                            </div>
                            {pendingMembers.map(renderMemberRow)}
                        </div>
                    )}
                </div>

                {/* Error message */}
                {errorMessage && (
                    <div className="text-[13px] mb-3" style={{ color: 'var(--color-error-bright)' }}>
                        {errorMessage}
                    </div>
                )}

                {renderInviteForm()}

                {/* Close button (for non-admin users who cannot see invite section) */}
                {!isOwnerOrAdmin && (
                    <div className="flex justify-end mt-3">
                        <TrebbleButton
                            onClick={onClose}
                            useEditorTheme={useEditorTheme}
                        >
                            {t('CANCEL')}
                        </TrebbleButton>
                    </div>
                )}
            </div>
        </TrebbleCard>
    );
};

WorkspaceMembersContent.propTypes = {
    members: PropTypes.arrayOf(
        PropTypes.shape({
            memberRecordId: PropTypes.string.isRequired,
            username: PropTypes.string,
            email: PropTypes.string,
            role: PropTypes.string.isRequired,
            isAdmin: PropTypes.bool,
            status: PropTypes.string,
        })
    ),
    isOwnerOrAdmin: PropTypes.bool.isRequired,
    seatLimit: PropTypes.number,
    seatUsed: PropTypes.number,
    mode: PropTypes.string,
    onInvite: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onSetRole: PropTypes.func.isRequired,
    onSetAdmin: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    i18nModule: PropTypes.object.isRequired,
    useEditorTheme: PropTypes.bool,
};

export { WorkspaceMembersContent };
export default WorkspaceMembersContent;
